'use client';

import { useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  Dialog,
  DialogContent,
  Grid,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  LocalShipping as ShippingIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Scale as WeightIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { LayoutContainer } from '@/kit/LayoutContainer';
import { useParcelsHistoryQuery } from '@/hooks/useParcelsQuery';
import { formatDate, formatDateLong, formatCurrency } from '@/utils/formatters';
import { brandingConfig } from '@/config/branding';
import { generateInvoiceHTML } from '@/utils/invoice/generateInvoiceHTML';
import { useTranslation } from '@/kit/useTranslation';

// Invoice/Receipt Component for Print
const PrintableInvoice = ({ parcel, onClose }) => {
  const { t } = useTranslation();
  const printRef = useRef();

  const handlePrint = useCallback(() => {
    const html = generateInvoiceHTML(parcel);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    // Give images (barcode/qr) a moment to load
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }, [parcel]);

  const handleDownloadPDF = useCallback(() => {
    // For PDF download, we'll trigger print with "Save as PDF" option
    handlePrint();
  }, [handlePrint]);

  // Using formatDateLong from @/utils/formatters for invoice display
  // formatCurrency imported from @/utils/formatters

  const getStatusClass = (status) => {
    const s = (status || '').toLowerCase().replace(/[^a-z]/g, '_');
    if (s.includes('complete') || s.includes('deliver')) return 'status-completed';
    if (s.includes('transit') || s.includes('pick')) return 'status-in_transit';
    return 'status-pending';
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t('invoices.invoicePreview')}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('invoices.print')}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#667eea',
              '&:hover': { bgcolor: '#5a6fd6' },
            }}
          >
            {t('invoices.downloadPdf')}
          </Button>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 0, height: '80vh', overflow: 'hidden' }}>
        <iframe
          title="Invoice Preview"
          srcDoc={generateInvoiceHTML(parcel)}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default function InvoicesPage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const user = useSelector((state) => state.auth.user);

  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParcel, setSelectedParcel] = useState(null);

  // Fetch parcels history (completed ones for invoices)
  const { data: parcels, isLoading, error } = useParcelsHistoryQuery();

  // Filter for completed/delivered parcels only (already filtered by history query, but double-check)
  const invoiceableParcels = (parcels || []).filter((p) => {
    const status = (p.parcelStatus || p.status || '').toUpperCase();
    return status.includes('DELIVERED') || status.includes('COMPLETED') || status.includes('PAID');
  });

  // Apply search filter
  const filteredParcels = invoiceableParcels.filter((p) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (p.parcelId || '').toLowerCase().includes(query) ||
      (p.parcelPickupAddress?.label || '').toLowerCase().includes(query) ||
      (p.parcelDeliveryAddress?.label || '').toLowerCase().includes(query)
    );
  });

  // Using formatDate and formatCurrency from @/utils/formatters

  return (
    <LayoutContainer>
      <Box sx={{ minHeight: '100vh', py: 3, bgcolor: '#f8fafc' }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <ReceiptIcon sx={{ fontSize: 32, color: '#667eea' }} />
              <Typography variant="h4" fontWeight={900} sx={{ color: '#0f172a' }}>
                {t('invoices.title')}
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{ color: '#64748b' }}>
              {t('invoices.subtitle')}
            </Typography>
          </Box>

          {/* Search */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              boxShadow: 'none',
              border: '1px solid #e2e8f0',
            }}
          >
            <TextField
              fullWidth
              placeholder={t('invoices.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fff',
                },
              }}
            />
          </Paper>

          {/* Content */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#667eea' }} />
            </Box>
          ) : error ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <Typography color="error">{t('invoices.failedToLoad')}</Typography>
            </Paper>
          ) : filteredParcels.length === 0 ? (
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: 3,
                boxShadow: 'none',
                border: '1px solid #e2e8f0',
              }}
            >
              <ReceiptIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                {t('invoices.noInvoices')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                {searchQuery ? t('invoices.noMatchSearch') : t('invoices.completeFirstShipment')}
              </Typography>
            </Paper>
          ) : isDesktop ? (
            // Desktop Table View
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 3,
                boxShadow: 'none',
                border: '1px solid #e2e8f0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                      {t('invoices.invoiceNumber')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                      {t('invoices.date')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                      {t('invoices.route')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                      {t('invoices.status')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>
                      {t('invoices.amount')}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#475569' }}>
                      {t('invoices.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredParcels.map((parcel) => (
                    <TableRow
                      key={parcel.parcelId}
                      sx={{
                        '&:hover': { bgcolor: '#f8fafc' },
                        transition: 'background 0.2s',
                      }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, color: '#0f172a' }}>
                          #{parcel.parcelId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: '#64748b' }}>
                          {formatDate(parcel.parcelCreatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ color: '#0f172a' }}>
                            {(parcel.parcelPickupAddress?.label || '').slice(0, 30)}
                            ...
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            → {(parcel.parcelDeliveryAddress?.label || '').slice(0, 30)}
                            ...
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={parcel.status || t('invoices.completed')}
                          sx={{
                            bgcolor: '#dcfce7',
                            color: '#166534',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {formatCurrency(parcel.parcelPrice)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ReceiptIcon />}
                            onClick={() => setSelectedParcel(parcel)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              borderColor: '#667eea',
                              color: '#667eea',
                              '&:hover': {
                                borderColor: '#667eea',
                                bgcolor: 'rgba(102, 126, 234, 0.08)',
                              },
                            }}
                          >
                            {t('invoices.viewInvoice')}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            // Mobile Card View
            <Stack spacing={2}>
              {filteredParcels.map((parcel) => (
                <Card
                  key={parcel.parcelId || parcel.id}
                  sx={{
                    borderRadius: 3,
                    boxShadow: 'none',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: '#0f172a',
                            fontSize: '1rem',
                          }}
                        >
                          #{parcel.parcelId}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {formatDate(parcel.parcelCreatedAt)}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={parcel.parcelStatus || t('invoices.completed')}
                        sx={{
                          bgcolor: '#dcfce7',
                          color: '#166534',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Stack>

                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationIcon sx={{ fontSize: 16, color: '#10b981' }} />
                        <Typography variant="body2" sx={{ color: '#0f172a' }} noWrap>
                          {parcel.parcelPickupAddress?.label || 'N/A'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                        <Typography variant="body2" sx={{ color: '#0f172a' }} noWrap>
                          {parcel.parcelDeliveryAddress?.label || 'N/A'}
                        </Typography>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.25rem',
                          color: '#0f172a',
                        }}
                      >
                        {formatCurrency(parcel.parcelPrice)}
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<ReceiptIcon />}
                        onClick={() => setSelectedParcel(parcel)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          bgcolor: '#667eea',
                          borderRadius: 2,
                          px: 2,
                          '&:hover': { bgcolor: '#5a6fd6' },
                        }}
                      >
                        {t('invoices.viewInvoice')}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Container>
      </Box>

      {/* Invoice Modal */}
      {selectedParcel && (
        <PrintableInvoice parcel={selectedParcel} onClose={() => setSelectedParcel(null)} />
      )}
    </LayoutContainer>
  );
}
